import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, IconButton, InputAdornment, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { BaseAPI } from "../utils/Api";
import { getItem } from "../utils/Helper";
import Notification from "../utils/Notification";
import ErrorHandler from "../utils/ErrorHandler";
import LoadingState from "../components/Controls/LoadingState";

import { FormProvider, RHFTextField } from "../components/hook-form";

import { ProfileSchema } from "../utils/SchemaValidation";

const Profile = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm({
    resolver: yupResolver(ProfileSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    setLoading(true);
    BaseAPI.get("/auth/profile", { headers: { Authorization: `Bearer ${getItem(localStorage, "at")}` } })
      .then((res) => {
        console.log(res.data.data);
        reset({
          firstName: res.data.data.f_name,
          lastName: res.data.data.l_name,
          email: res.data.data.email,
          phone: res.data.data.phone,
        });
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          ErrorHandler(err?.response?.data?.message, navigate);
        } else {
          Notification("Something went wrong! Please check your internet", "error");
        }
      })
      .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data) => {
    const body = {
      f_name: data.firstName,
      l_name: data.lastName,
      phone: data.phone,
      new_pass: data.password || "",
      old_pass: data.oldPassword || "",
      mfa_enables: false,
    };

    setLoading(true);
    BaseAPI.patch("/auth/profile", body, { headers: { Authorization: `Bearer ${getItem(localStorage, "at")}` } })
      .then((res) => {
        console.log(res);
        Notification("Profile updated", "success");
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          ErrorHandler(err?.response?.data?.message, navigate);
        } else {
          Notification("Something went wrong! Please check your internet", "error");
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <LoadingState loading={loading} />
      <Typography variant='h4' mt={2} mb={4} className='bold'>
        Profile Information
      </Typography>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container columnSpacing={5} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='firstName' label='First Name' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='lastName' label='Last Name' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='email' label='Email' disabled />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='phone' label='Phone Number' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField
              name='password'
              label='New password'
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton aria-label='toggle password visibility' onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='oldPassword' label='Current password to confirm new password' type='password' />
          </Grid>
        </Grid>
        <Box display='flex' justifyContent='center' mt={4}>
          <Button type='submit' variant='contained' disableElevation disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} color='primary' /> : "Update Profile"}
          </Button>
        </Box>
      </FormProvider>
    </>
  );
};

export default Profile;
